/**
 * Billing → Wallet. Prepaid balance that pays for AI replies and/or WhatsApp
 * messages — switched on per workspace by the platform (independent of the
 * plan). Top-ups go through Cashfree; the result is resolved server-side by
 * useWalletTopup + TopupResultDialog.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Lock,
  Refresh,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import StatusChip from '@/components/ui/StatusChip';
import walletApi, {
  formatInr,
  type TopupListRow,
  type TopupState,
  type WalletJournalKind,
  type WalletJournalRow,
  type WalletStatementLine,
  walletErrorMessage,
} from '@/services/wallet.api';
import { refreshWalletSummary, useWalletSummary } from '@/hooks/useWalletSummary';
import { normalizeIndianMobile, useWalletTopup } from '@/hooks/useWalletTopup';
import TopupResultDialog from '@/components/billing/wallet/TopupResultDialog';
import { validateTopupAmount } from '@/utils/walletTopupPolling';

const CREDIT_KINDS = new Set<WalletJournalKind>(['topup', 'adjustment_credit', 'promo_grant']);

const FILTERS: Array<{ label: string; kinds?: WalletJournalKind[] }> = [
  { label: 'All' },
  { label: 'Top-ups', kinds: ['topup'] },
  { label: 'AI', kinds: ['usage_ai'] },
  { label: 'WhatsApp', kinds: ['usage_meta'] },
  { label: 'Adjustments', kinds: ['adjustment_credit', 'adjustment_debit', 'promo_grant', 'refund'] },
];

const META_CATEGORY_LABEL: Record<string, string> = {
  marketing: 'Marketing',
  utility: 'Utility',
  authentication: 'Authentication',
  authentication_international: 'Authentication (intl.)',
  service: 'Service',
};

// `status` is a StatusChip palette key — quiet dot + label, not a saturated fill.
const TOPUP_STATE_CHIP: Record<TopupState, { label: string; status: string }> = {
  credited: { label: 'Credited', status: 'success' },
  crediting: { label: 'Paid · crediting', status: 'info' },
  processing: { label: 'Processing', status: 'warning' },
  awaiting: { label: 'Not completed', status: 'inactive' },
  cancelled: { label: 'Cancelled', status: 'inactive' },
  failed: { label: 'Failed', status: 'error' },
  expired: { label: 'Expired', status: 'inactive' },
};

const RECENT_TOPUPS = 5;
const PAGE_SIZES = [10, 25, 50];

function describeRow(r: WalletJournalRow): { title: string; detail?: string | undefined } {
  switch (r.kind) {
    case 'topup':
      return { title: 'Wallet top-up' };
    case 'usage_ai':
      return { title: 'AI usage', detail: r.model };
    case 'usage_meta':
      return {
        title: 'WhatsApp message',
        detail: [r.category ? META_CATEGORY_LABEL[r.category] ?? r.category : undefined, r.country].filter(Boolean).join(' · '),
      };
    case 'adjustment_credit':
      return { title: 'Credit', detail: r.reason };
    case 'adjustment_debit':
      return { title: 'Debit', detail: r.reason };
    case 'promo_grant':
      return { title: 'Promotional credit', detail: r.reason };
    case 'refund':
      return { title: 'Refund', detail: r.reason };
    default:
      return { title: r.kind };
  }
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-IN')} ${n === 1 ? one : many}`;

const categoryLabel = (c?: string) => (c ? META_CATEGORY_LABEL[c] ?? c : undefined);

/** Statement row text: groups summarise, singles read like the journal row they are. */
function describeLine(l: WalletStatementLine): { title: string; detail?: string | undefined } {
  if (l.group === 'campaign') {
    return { title: `Campaign · ${l.campaignName || 'Untitled campaign'}`, detail: plural(l.count, 'message charged', 'messages charged') };
  }
  if (l.group === 'day' && l.kind === 'usage_meta') {
    return {
      title: 'WhatsApp messages',
      detail: [categoryLabel(l.category), l.country, plural(l.count, 'message', 'messages')].filter(Boolean).join(' · '),
    };
  }
  if (l.group === 'day' && l.kind === 'usage_ai') return { title: 'AI usage', detail: plural(l.count, 'reply', 'replies') };
  return describeRow({ kind: l.kind, reason: l.reason, model: l.model } as WalletJournalRow);
}

const formatDay = (day?: string) => {
  if (!day) return '—';
  const d = new Date(`${day}T00:00:00+05:30`);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const meterLabel = {
  ai: { off: null, wallet: 'AI replies', quota_then_wallet: 'AI beyond your plan allowance' },
  whatsapp: { off: null, platform_fee: 'WhatsApp messages', pass_through: 'WhatsApp messages' },
} as const;

/** One label/value line of the breakdown card. */
const BreakdownRow: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25 }}>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow>
          <InfoOutlined sx={{ fontSize: 15, color: 'text.disabled' }} />
        </Tooltip>
      )}
    </Stack>
    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </Typography>
  </Stack>
);

/** Signed amount text: credits +, everything else −; sub-rupee sums keep paise precision. */
const formatSigned = (kind: WalletJournalKind, amount: number) => {
  const abs = Math.abs(amount);
  return `${CREDIT_KINDS.has(kind) ? '+' : '−'}${formatInr(abs, { decimals: abs > 0 && abs < 1 ? 4 : 2 })}`;
};

const ENTRIES_PAGE = 50;

/**
 * One statement row. Groups (campaign run / day) expand to their individual
 * charges, fetched on first open and paged with "Show more".
 */
const StatementRow: React.FC<{ line: WalletStatementLine }> = ({ line }) => {
  const expandable = line.group !== 'single';
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<WalletJournalRow[] | null>(null);
  const [next, setNext] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  const load = useCallback(
    async (beforeSeq?: number) => {
      const req = ++reqRef.current;
      setLoading(true);
      setError(null);
      try {
        const res = await walletApi.getStatementEntries(line.id, { beforeSeq, limit: ENTRIES_PAGE });
        if (req !== reqRef.current) return; // superseded (e.g. the row changed while a page was loading)
        setEntries((prev) => (beforeSeq && prev ? [...prev, ...res.rows] : res.rows));
        setNext(res.nextBeforeSeq);
      } catch (err) {
        if (req === reqRef.current) setError(walletErrorMessage(err, 'Could not load the charges.'));
      } finally {
        if (req === reqRef.current) setLoading(false);
      }
    },
    [line.id]
  );

  // A refresh can grow an open group (same id, new charges): the cached charges are then
  // stale — reload them if the row is open, otherwise drop them so the next open fetches.
  const version = `${line.count}:${line.amount}`;
  const seenVersion = useRef(version);
  useEffect(() => {
    if (seenVersion.current === version) return;
    seenVersion.current = version;
    if (open) void load();
    else {
      reqRef.current++;
      setEntries(null);
      setNext(undefined);
      setLoading(false);
    }
  }, [version, open, load]);

  const toggle = () => {
    if (!expandable) return;
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && entries === null && !loading) void load();
  };

  const d = describeLine(line);
  const credit = CREDIT_KINDS.has(line.kind);
  const when =
    line.group === 'day'
      ? formatDay(line.day)
      : line.group === 'campaign'
        ? formatDateTime(line.lastAt)
        : formatDateTime(line.firstAt);

  return (
    <>
      <TableRow
        hover={expandable}
        onClick={toggle}
        sx={{ cursor: expandable ? 'pointer' : 'default', '& > td': open ? { borderBottom: 0 } : undefined }}
      >
        <TableCell sx={{ pl: 3, whiteSpace: 'nowrap', color: 'text.secondary' }}>{when}</TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 24, flexShrink: 0, display: 'flex', ml: -0.5 }}>
              {expandable && (
                <IconButton
                  size="small"
                  aria-label={open ? 'Hide charges' : 'Show charges'}
                  aria-expanded={open}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle();
                  }}
                  sx={{ p: 0.25 }}
                >
                  {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                </IconButton>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500} noWrap title={d.title}>
                {d.title}
              </Typography>
              {d.detail && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {d.detail}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>
        <TableCell
          align="right"
          sx={{ whiteSpace: 'nowrap', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: credit ? 'success.dark' : 'text.primary' }}
        >
          {formatSigned(line.kind, line.amount)}
        </TableCell>
        <TableCell align="right" sx={{ pr: 3, whiteSpace: 'nowrap', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {formatInr(line.balanceAfter)}
        </TableCell>
      </TableRow>
      {expandable && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, borderBottom: open ? undefined : 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: 'action.hover', pl: 7.5, pr: 3, py: 1 }}>
                {error ? (
                  <Alert
                    severity="error"
                    sx={{ my: 1 }}
                    action={
                      <Button color="inherit" size="small" onClick={() => void load(entries?.length ? next : undefined)}>
                        Retry
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                ) : entries === null ? (
                  <Stack spacing={1} sx={{ py: 1 }}>
                    <Skeleton width="60%" />
                    <Skeleton width="45%" />
                  </Stack>
                ) : (
                  <>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {entries.map((r) => {
                        const rd = describeRow(r);
                        return (
                          <Stack
                            component="li"
                            key={r.id}
                            direction="row"
                            alignItems="baseline"
                            spacing={2}
                            sx={{ py: 0.75, '& + &': { borderTop: 1, borderColor: 'divider' } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 64, flexShrink: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                            >
                              {line.group === 'day' ? formatTime(r.occurredAt) : formatDateTime(r.occurredAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                              {rd.detail || rd.title}
                            </Typography>
                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {formatSigned(r.kind, r.amount)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Box>
                    {next !== undefined && (
                      <Button size="small" disabled={loading} onClick={() => void load(next)} sx={{ mt: 0.5, ml: -1 }}>
                        {loading ? 'Loading…' : `Show more (${Math.max(0, line.count - entries.length).toLocaleString('en-IN')} left)`}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const BillingWallet: React.FC = () => {
  const { summary, loaded, error: summaryError, refresh } = useWalletSummary();

  // ── History ── keyset-paged by `seq`: cursors[p] is the beforeSeq that opens page p,
  // learnt from page p-1's nextBeforeSeq (no total count — the ledger only grows).
  const [filter, setFilter] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<WalletStatementLine[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [topups, setTopups] = useState<TopupListRow[]>([]);
  const cursorsRef = useRef<Array<number | undefined>>([undefined]);
  const journalReq = useRef(0);

  const loadPage = useCallback(
    async (p: number) => {
      const req = ++journalReq.current;
      setJournalLoading(true);
      setJournalError(null);
      try {
        const res = await walletApi.getStatement({
          beforeSeq: cursorsRef.current[p],
          limit: rowsPerPage,
          kind: FILTERS[filter]?.kinds,
        });
        if (req !== journalReq.current) return; // a newer page/filter request superseded this one
        cursorsRef.current = [...cursorsRef.current.slice(0, p + 1), res.nextBeforeSeq];
        setRows(res.rows);
        setHasNext(Boolean(res.nextBeforeSeq));
        setPage(p);
      } catch (err) {
        if (req === journalReq.current) setJournalError(walletErrorMessage(err, 'Could not load wallet history.'));
      } finally {
        if (req === journalReq.current) setJournalLoading(false);
      }
    },
    [filter, rowsPerPage]
  );

  const reloadFirstPage = useCallback(() => {
    cursorsRef.current = [undefined];
    void loadPage(0);
  }, [loadPage]);

  const loadTopups = useCallback(async () => {
    try {
      setTopups(await walletApi.listTopups(RECENT_TOPUPS));
    } catch {
      /* secondary list — the page stays usable without it */
    }
  }, []);

  const walletOn = Boolean(summary?.enabled);
  useEffect(() => {
    if (walletOn) reloadFirstPage();
  }, [walletOn, reloadFirstPage]);
  useEffect(() => {
    if (walletOn) void loadTopups();
  }, [walletOn, loadTopups]);

  const refreshAll = useCallback(() => {
    void refreshWalletSummary(true);
    reloadFirstPage();
    void loadTopups();
  }, [reloadFirstPage, loadTopups]);

  // ── Top-up ──
  const topup = useWalletTopup({ onCredited: refreshAll, onSettled: () => void loadTopups() });
  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const presets = useMemo(() => {
    if (!summary) return [];
    const { min, max } = summary.topup;
    const candidates = [500, 1000, 2000, 5000, 10000];
    const list = candidates.filter((v) => v >= min && v <= max);
    return list.length ? list.slice(0, 4) : [min];
  }, [summary]);

  const submitTopup = () => {
    if (!summary) return;
    const v = validateTopupAmount(amountInput, summary.topup.min, summary.topup.max);
    if (v.error) {
      setAmountError(v.error);
      return;
    }
    setAmountError(null);
    void topup.startTopup(v.amount!);
  };

  const submitPhone = () => {
    const phone = normalizeIndianMobile(phoneInput);
    if (!phone) {
      setPhoneError('Enter a valid 10-digit Indian mobile number');
      return;
    }
    setPhoneError(null);
    topup.submitPhone(phone);
  };

  // ── Render ──
  if (!loaded) {
    return (
      <Box>
        <Skeleton variant="text" width={220} height={44} />
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {[5, 7].map((md) => (
            <Grid item xs={12} md={md} key={md}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const header = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
      <Box>
        <Typography variant="h4" fontWeight="bold">
          Wallet
        </Typography>
        <Typography color="text.secondary">Prepaid balance for usage billed to this workspace</Typography>
      </Box>
      {walletOn && (
        <Tooltip title="Refresh">
          <IconButton onClick={refreshAll} aria-label="Refresh wallet">
            <Refresh />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  // The result dialog must render even if the summary failed — a returning
  // customer always gets an answer about their payment.
  const resultDialog = (
    <TopupResultDialog
      verify={topup.verify}
      retrying={topup.starting}
      onClose={topup.dismiss}
      onRetry={(s) => void topup.retry(s)}
      onCheckAgain={(id, amount) => void topup.verifyOrder(id, amount)}
      onNewTopup={() => {
        topup.dismiss();
        document.getElementById('wallet-topup-amount')?.focus();
      }}
    />
  );

  if (summaryError && !summary) {
    return (
      <Box>
        {header}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refresh()}>
              Retry
            </Button>
          }
        >
          Could not load your wallet. Please try again.
        </Alert>
        {resultDialog}
      </Box>
    );
  }

  if (!summary?.enabled) {
    return (
      <Box>
        {header}
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceWallet sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              The prepaid wallet isn't enabled for this workspace
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', mt: 1 }}>
              Your usage is covered by your plan. Contact your account manager if you'd like to pay for AI or WhatsApp
              usage from a prepaid balance.
            </Typography>
          </CardContent>
        </Card>
        {resultDialog}
      </Box>
    );
  }

  const frozen = summary.status === 'frozen';
  const meters = [meterLabel.ai[summary.meters.ai], meterLabel.whatsapp[summary.meters.whatsapp]].filter(Boolean) as string[];
  const exhausted = summary.available + summary.creditLimit <= 0;
  const canTopUp = summary.topup.allowSelfServe && !frozen;

  return (
    <Box>
      {header}

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {frozen && (
          <Alert severity="error" icon={<Lock />}>
            This wallet is frozen. Usage billed to it is paused. Please contact support.
          </Alert>
        )}
        {!frozen && exhausted && (
          <Alert severity="error">
            Your wallet balance is used up. {meters.join(' and ')} {meters.length > 1 ? 'are' : 'is'} paused until you top
            up.
          </Alert>
        )}
        {!frozen && !exhausted && summary.lowBalance && (
          <Alert severity="warning">
            Your balance is running low (below {formatInr(summary.lowBalanceThreshold, { decimals: 0 })}). Top up to avoid
            interruptions.
          </Alert>
        )}
      </Stack>

      <Grid container spacing={3}>
        {/* Balance */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Available balance
              </Typography>
              <Typography
                variant="h3"
                fontWeight={700}
                color={exhausted ? 'error.main' : 'text.primary'}
                sx={{ mt: 0.5, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatInr(summary.available)}
              </Typography>
              {summary.inFlight > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatInr(summary.inFlight)} held for messages in progress
                </Typography>
              )}
              {summary.forecast && summary.forecast.burnPerDay > 0 && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1.5 }}
                  color={(summary.forecast.daysLeft ?? 0) < 3 ? 'warning.dark' : 'text.secondary'}
                >
                  {summary.forecast.daysLeft !== undefined
                    ? `Lasts about ${summary.forecast.daysLeft} day${summary.forecast.daysLeft === 1 ? '' : 's'}`
                    : 'Usage'}{' '}
                  at {formatInr(summary.forecast.burnPerDay)}/day
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
                Last activity {formatDateTime(summary.lastActivityAt)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Breakdown */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Balance breakdown
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <BreakdownRow label="Paid balance">{formatInr(summary.paid)}</BreakdownRow>
                {summary.promo > 0 && (
                  <>
                    <Divider />
                    <BreakdownRow label="Promotional credit" hint="Used before your paid balance.">
                      {formatInr(summary.promo)}
                    </BreakdownRow>
                  </>
                )}
                {(summary.conversationReserve ?? 0) > 0 && (
                  <>
                    <Divider />
                    <BreakdownRow
                      label="Reserved for conversations"
                      hint="Kept aside so AI replies, flows and API messages keep working. Campaigns can't spend it."
                    >
                      {formatInr(summary.conversationReserve ?? 0)}
                    </BreakdownRow>
                  </>
                )}
                {summary.creditLimit > 0 && (
                  <>
                    <Divider />
                    <BreakdownRow
                      label="Credit limit"
                      hint="Conversations can continue up to this amount below zero. Campaigns never use it."
                    >
                      {formatInr(summary.creditLimit)}
                    </BreakdownRow>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top up */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600}>
                Add money
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Credited to your wallet as soon as the payment is confirmed.
              </Typography>
              {!summary.topup.allowSelfServe ? (
                <Alert severity="info">Top-ups for this workspace are handled by your account manager.</Alert>
              ) : (
                <>
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {presets.map((p) => {
                      const selected = amountInput === String(p);
                      return (
                        <Chip
                          key={p}
                          label={formatInr(p, { decimals: 0 })}
                          clickable
                          variant="outlined"
                          disabled={!canTopUp}
                          color={selected ? 'primary' : 'default'}
                          sx={{ fontWeight: 600, ...(selected ? {} : { borderColor: 'divider' }) }}
                          onClick={() => {
                            setAmountInput(String(p));
                            setAmountError(null);
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <TextField
                    id="wallet-topup-amount"
                    fullWidth
                    label="Amount"
                    value={amountInput}
                    disabled={!canTopUp || topup.starting}
                    onChange={(e) => {
                      setAmountInput(e.target.value.replace(/[^\d.]/g, ''));
                      setAmountError(null);
                      topup.clearStartError();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitTopup();
                    }}
                    inputProps={{ inputMode: 'decimal', 'aria-describedby': 'wallet-topup-help' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    error={Boolean(amountError)}
                    helperText={
                      amountError ||
                      `Between ${formatInr(summary.topup.min, { decimals: 0 })} and ${formatInr(summary.topup.max, { decimals: 0 })}`
                    }
                    FormHelperTextProps={{ id: 'wallet-topup-help' }}
                  />
                  {topup.startError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={topup.clearStartError}>
                      {topup.startError}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                    disabled={!canTopUp || topup.starting || !amountInput}
                    onClick={submitTopup}
                    startIcon={topup.starting ? <CircularProgress size={18} color="inherit" /> : <Lock fontSize="small" />}
                  >
                    {topup.starting
                      ? 'Opening secure payment…'
                      : `Pay ${amountInput && !validateTopupAmount(amountInput, summary.topup.min, summary.topup.max).error ? formatInr(Number(amountInput)) : ''}`.trim()}
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, textAlign: 'center' }}>
                    Payments are processed securely by Cashfree.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent top-ups */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  Recent top-ups
                </Typography>
                {topups.length > 0 && (
                  <Button size="small" onClick={() => {
                      setFilter(FILTERS.findIndex((f) => f.label === 'Top-ups'));
                      document.getElementById('wallet-transactions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}>
                    View all
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Your last {RECENT_TOPUPS} payments
              </Typography>
            </CardContent>
            {topups.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, py: 5, px: 3, textAlign: 'center' }}>
                <ReceiptLongOutlined sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">
                  No top-ups yet. Payments you make will appear here.
                </Typography>
              </Stack>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, px: 1 }}>
                {topups.map((t, i) => {
                  const chip = TOPUP_STATE_CHIP[t.state];
                  const checkable = t.state === 'awaiting' || t.state === 'processing' || t.state === 'crediting';
                  return (
                    <Box component="li" key={t.gatewayOrderId}>
                      {i > 0 && <Divider sx={{ mx: 2 }} />}
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, py: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatInr(t.amount)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(t.paidAt ?? t.createdAt)}
                          </Typography>
                        </Box>
                        {checkable && (
                          <Button size="small" variant="text" onClick={() => void topup.verifyOrder(t.gatewayOrderId, t.amount)}>
                            Check status
                          </Button>
                        )}
                        <StatusChip status={chip.status} label={chip.label} dot />
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>
        </Grid>

        {/* History */}
        <Grid item xs={12} id="wallet-transactions">
          <Card>
            <CardContent sx={{ pb: 0 }}>
              <Typography variant="h6" fontWeight={600}>
                Transactions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Campaigns, daily usage and top-ups, newest first. Expand a row to see each charge.
              </Typography>
              <Tabs
                value={filter}
                onChange={(_, v) => setFilter(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500 } }}
              >
                {FILTERS.map((f) => (
                  <Tab key={f.label} label={f.label} />
                ))}
              </Tabs>
            </CardContent>
            <Box sx={{ height: 2 }}>{journalLoading && rows.length > 0 && <LinearProgress sx={{ height: 2 }} />}</Box>
            {journalError && (
              <Alert
                severity="error"
                sx={{ mx: 3, my: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => void loadPage(page)}>
                    Retry
                  </Button>
                }
              >
                {journalError}
              </Alert>
            )}
            {rows.length === 0 && journalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : rows.length === 0 && !journalError ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
                <ReceiptLongOutlined sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">
                  {filter === 0 ? 'No transactions yet.' : `No ${FILTERS[filter]?.label.toLowerCase()} transactions yet.`}
                </Typography>
              </Stack>
            ) : (
              rows.length > 0 && (
                <>
                  <Box sx={{ overflowX: 'auto', opacity: journalLoading ? 0.6 : 1, transition: 'opacity .15s' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 3 }}>Date</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right" sx={{ pr: 3 }}>
                            Balance
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((l) => (
                          <StatementRow key={l.id} line={l} />
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  <Divider />
                  <TablePagination
                    component="div"
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={PAGE_SIZES}
                    // Unknown total while older pages exist (-1); exact once the last page is reached.
                    count={hasNext ? -1 : page * rowsPerPage + rows.length}
                    labelDisplayedRows={({ from, to, count }) => (count === -1 ? `${from}–${to}` : `${from}–${to} of ${count}`)}
                    onPageChange={(_, p) => {
                      if (!journalLoading) void loadPage(p);
                    }}
                    onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
                    nextIconButtonProps={{ disabled: !hasNext || journalLoading }}
                    backIconButtonProps={{ disabled: page === 0 || journalLoading }}
                  />
                </>
              )
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Cashfree needs a customer phone — asked only when none is on file. */}
      <Dialog open={topup.needsPhoneFor !== null} onClose={topup.cancelPhonePrompt} maxWidth="xs" fullWidth>
        <DialogTitle>Phone number required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cashfree needs a phone number to process your payment securely (used for payment verification only).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Phone number"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              setPhoneError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitPhone();
            }}
            error={Boolean(phoneError)}
            helperText={phoneError || undefined}
            inputProps={{ inputMode: 'tel' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={topup.cancelPhonePrompt}>Cancel</Button>
          <Button variant="contained" onClick={submitPhone}>
            Continue to payment
          </Button>
        </DialogActions>
      </Dialog>

      {resultDialog}
    </Box>
  );
};

export default BillingWallet;
